import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCustomers } from '../context/CustomerContext';
import VehicleModal from '../components/VehicleModal';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlus, FiSearch, FiTruck, FiDollarSign, FiCalendar, FiTag, FiFilter } = FiIcons;

function Inventory() {
  const { state, dispatch } = useCustomers();
  const { vehicles, vehicleTypes } = state;
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filteredVehicles = vehicles.filter(vehicle => {
    const matchesSearch = vehicle.make?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.year?.toString().includes(searchTerm) ||
      vehicle.vin?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || vehicle.status === statusFilter;
    const matchesType = typeFilter === 'all' || vehicle.type === typeFilter;

    return matchesSearch && matchesStatus && matchesType;
  });

  const stats = [
    {
      name: 'Total Vehicles',
      value: vehicles.length,
      icon: FiTruck,
      color: 'bg-blue-500',
    },
    {
      name: 'Available',
      value: vehicles.filter(v => v.status === 'available').length,
      icon: FiTag,
      color: 'bg-green-500',
    },
    {
      name: 'Sold',
      value: vehicles.filter(v => v.status === 'sold').length,
      icon: FiDollarSign,
      color: 'bg-purple-500',
    },
    {
      name: 'Average Price',
      value: vehicles.length > 0
        ? `$${Math.round(vehicles.reduce((total, v) => total + (v.price || 0), 0) / vehicles.length).toLocaleString()}`
        : '$0',
      icon: FiCalendar,
      color: 'bg-orange-500',
    },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'bg-green-100 text-green-700';
      case 'sold': return 'bg-purple-100 text-purple-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'maintenance': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white p-3 sm:p-4 lg:p-6 rounded-lg sm:rounded-xl shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">{stat.name}</p>
                <p className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 mt-1 sm:mt-2 truncate">{stat.value}</p>
              </div>
              <div className={`${stat.color} p-2 sm:p-3 rounded-lg flex-shrink-0 ml-2`}>
                <SafeIcon icon={stat.icon} className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Header Actions */}
      <div className="flex flex-col space-y-3 sm:space-y-4">
        {/* Search and Add Button Row */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div className="flex items-center space-x-2 sm:space-x-4 flex-1">
            <div className="relative flex-1 sm:flex-none">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search vehicles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent w-full sm:w-64"
              />
            </div>
            
            {/* Mobile filter toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors sm:hidden"
            >
              <SafeIcon icon={FiFilter} className="w-5 h-5" />
            </button>
          </div>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setIsModalOpen(true)}
            className="bg-primary-600 text-white px-3 sm:px-4 py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors flex items-center space-x-2 text-sm sm:text-base"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4" />
            <span className="hidden sm:inline">Add Vehicle</span>
            <span className="sm:hidden">Add</span>
          </motion.button>
        </div>

        {/* Filters Row - Desktop always visible, Mobile collapsible */}
        <div className={`flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 ${showFilters ? 'block' : 'hidden sm:flex'}`}>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          >
            <option value="all">All Status</option>
            <option value="available">Available</option>
            <option value="sold">Sold</option>
            <option value="pending">Pending</option>
            <option value="maintenance">Maintenance</option>
          </select>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          >
            <option value="all">All Types</option>
            {vehicleTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Vehicle Grid */}
      {filteredVehicles.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {filteredVehicles.map((vehicle, index) => (
            <motion.div
              key={vehicle.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
            >
              <div className="aspect-w-16 aspect-h-9 bg-gray-200">
                {vehicle.imageUrl ? (
                  <img
                    src={vehicle.imageUrl}
                    alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                    className="w-full h-32 sm:h-48 object-cover"
                  />
                ) : (
                  <div className="w-full h-32 sm:h-48 bg-gray-200 flex items-center justify-center">
                    <SafeIcon icon={FiTruck} className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400" />
                  </div>
                )}
              </div>
              
              <div className="p-4 sm:p-6">
                <div className="flex items-start justify-between mb-3 sm:mb-4">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm sm:text-lg font-semibold text-gray-900 truncate">
                      {vehicle.year} {vehicle.make} {vehicle.model}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-500">{vehicle.type}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(vehicle.status)} ml-2 flex-shrink-0`}>
                    {vehicle.status}
                  </span>
                </div>
                
                <div className="space-y-1 sm:space-y-2 mb-3 sm:mb-4">
                  {vehicle.mileage && (
                    <p className="text-xs sm:text-sm text-gray-600">
                      <span className="font-medium">Mileage:</span> {vehicle.mileage.toLocaleString()} miles
                    </p>
                  )}
                  {vehicle.color && (
                    <p className="text-xs sm:text-sm text-gray-600">
                      <span className="font-medium">Color:</span> {vehicle.color}
                    </p>
                  )}
                  {vehicle.vin && (
                    <p className="text-xs sm:text-sm text-gray-600 font-mono truncate">
                      <span className="font-medium">VIN:</span> {vehicle.vin}
                    </p>
                  )}
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="text-lg sm:text-2xl font-bold text-green-600">
                    ${vehicle.price?.toLocaleString() || 'Price TBD'}
                  </div>
                  <div className="flex space-x-1 sm:space-x-2">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="px-2 sm:px-3 py-1 bg-primary-50 text-primary-600 rounded-md text-xs sm:text-sm font-medium hover:bg-primary-100 transition-colors"
                    >
                      <span className="hidden sm:inline">View Details</span>
                      <span className="sm:hidden">View</span>
                    </motion.button>
                    {vehicle.status === 'available' && (
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="px-2 sm:px-3 py-1 bg-green-50 text-green-600 rounded-md text-xs sm:text-sm font-medium hover:bg-green-100 transition-colors"
                      >
                        <span className="hidden sm:inline">Mark Sold</span>
                        <span className="sm:hidden">Sold</span>
                      </motion.button>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 sm:py-12">
          <SafeIcon icon={FiTruck} className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No vehicles found</h3>
          <p className="text-gray-500 mb-6 text-sm sm:text-base px-4">
            {searchTerm || statusFilter !== 'all' || typeFilter !== 'all'
              ? 'Try adjusting your search or filter criteria'
              : 'Start building your inventory by adding your first vehicle'}
          </p>
          {!searchTerm && statusFilter === 'all' && typeFilter === 'all' && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsModalOpen(true)}
              className="bg-primary-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors text-sm sm:text-base"
            >
              Add Your First Vehicle
            </motion.button>
          )}
        </div>
      )}

      {/* Vehicle Modal */}
      <VehicleModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
}

export default Inventory;